import { AgGridReact } from "@ag-grid-community/react";
import React, {
  RefObject,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { useAgGridEventState } from "shared/components/table/ag/AgGridEventState";
import { MaybernTableSizes } from "shared/components/table/maybern/MaybernTableConstants";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import { uninitializedContextErrorFn } from "shared/errors/UninitializedContextError";
import { useWindowSize } from "usehooks-ts";

export interface MaybernTableUIContextValues {
  agGrid: AgGridReact | null;
  agGridRef: RefObject<AgGridReact>;
  topLevelRowCount: number;
  autoHeightMaxRowCount: number;
  autoHeightHiddenRowCount: number;
  isReady: boolean;
  setIsReady: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: () => void;
}

const throwUninitializedContextError = uninitializedContextErrorFn(
  "MaybernTableUIContext",
);

export const MaybernTableUIContext =
  React.createContext<MaybernTableUIContextValues>({
    agGridRef: undefined as never,
    agGrid: null,
    topLevelRowCount: 0,
    autoHeightMaxRowCount: 0,
    autoHeightHiddenRowCount: 0,
    isReady: false,
    setIsReady: throwUninitializedContextError,
    refresh: () => {},
  });

export const useMaybernTableUIContext = () => useContext(MaybernTableUIContext);

export const MaybernTableUIProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const agGridRef = useRef<AgGridReact | null>(null);
  const agGrid =
    isReady && tryGetAgGridApi(agGridRef.current) ? agGridRef.current : null;

  if (isReady && !agGrid) {
    setIsReady(false);
  }

  const { height: windowHeight } = useWindowSize();

  const rowHeight = MaybernTableSizes.rowHeight;
  const maxHeight = windowHeight * 0.4;

  const autoHeightMaxRowCount = Math.max(Math.floor(maxHeight / rowHeight), 4);

  // this is used to determine if the underlying table data is empty,
  // so count ignores filters (non-displayed rows)
  const topLevelRowCount =
    useAgGridEventState(agGrid, "modelUpdated", (api) => {
      let count = 0;
      api.forEachNode((node) => {
        if (node.level === 0) {
          count++;
        }
      });
      return count;
    }) ?? 0;

  const displayedRowCount =
    useAgGridEventState(agGrid, "modelUpdated", (api) =>
      api.getDisplayedRowCount(),
    ) ?? 0;

  const visibleRowCount = Math.min(autoHeightMaxRowCount, displayedRowCount);

  const autoHeightHiddenRowCount = displayedRowCount - visibleRowCount;

  return (
    <MaybernTableUIContext.Provider
      value={{
        agGrid,
        agGridRef,
        topLevelRowCount,
        autoHeightMaxRowCount,
        autoHeightHiddenRowCount,
        isReady,
        setIsReady,
        refresh,
      }}
    >
      {children}
    </MaybernTableUIContext.Provider>
  );
};
